import { type RefObject, useLayoutEffect, useState } from 'react';

export type SlidingPillRect = {
    readonly left: number;
    readonly top: number;
    readonly width: number;
    readonly height: number;
};

/**
 * Position of the sliding active-pill inside a segmented control or tab
 * strip. Measures the active element's offset box within the track, so the
 * result is independent of page or container scroll — unlike FLIP-based
 * layout animations, which mismeasure inside scrolled containers. Returns
 * null until the track has rendered an active element; re-measures when the
 * active key changes or the track resizes (e.g. wrapping, font load).
 *
 * The track must be the active element's `offsetParent` (position it
 * relative) so the returned offsets line up with an absolutely positioned
 * pill inside it.
 */
export function useSlidingPill(
    trackRef: RefObject<HTMLElement | null>,
    activeSelector: string,
    activeKey: string | undefined,
): SlidingPillRect | null {
    const [rect, setRect] = useState<SlidingPillRect | null>(null);

    useLayoutEffect(() => {
        const track = trackRef.current;
        if (track === null) {
            setRect(null);
            return;
        }
        const observer = new ResizeObserver(() => measure());
        const measure = () => {
            const active = track.querySelector(activeSelector);
            if (!(active instanceof HTMLElement)) {
                setRect(null);
                return;
            }
            // `observe` is idempotent per element, and the shared `disconnect`
            // below releases both the track and whichever trigger is active.
            observer.observe(active);
            setRect({
                left: active.offsetLeft,
                top: active.offsetTop,
                width: active.offsetWidth,
                height: active.offsetHeight,
            });
        };
        measure();
        observer.observe(track);
        return () => observer.disconnect();
    }, [trackRef, activeSelector, activeKey]);

    return rect;
}
